import { CHECK_INVENTORY } from './checks.data';

export interface CheckItem {
  title: string;
  wcag: string[];
  /** 'A' | 'AA' | 'AAA' | 'best-practice' (string: generated JSON). */
  level: string;
}

export interface CheckCategory {
  id: string;
  name: string;
  description: string;
  /** 'automated' | 'interactive' | 'ai-assisted'. */
  method: string;
  checks: CheckItem[];
}

export interface CheckInventory {
  generatedFrom: string;
  totals: { checks: number; wcagSCsCovered: number };
  wcagCoverage: { A: string[]; AA: string[]; AAA_suggestions: string[] };
  categories: CheckCategory[];
}

/** WCAG 2.2 has 31 Level A and 24 Level AA success criteria (4.1.1 Parsing
 *  is obsolete and not counted). */
export const WCAG22_A_AA_TOTAL = 55;

/** How a category's candidates are found. Every finding, whichever way it
 *  was found, is then verified by a specialist before it reaches a report. */
export const METHOD_LABELS: Record<string, string> = {
  'ai-assisted': 'AI-assisted',
  automated: 'Automated',
  interactive: 'Interactive',
};

/**
 * WCAG 2.2 A/AA criteria the tooling does not test — assessed by hand by a
 * specialist in every audit. Derived from the inventory: the 55 A/AA
 * criteria minus `wcagCoverage.A` and `.AA`. Keep in step when it changes.
 */
export const MANUAL_CRITERIA: { sc: string; name: string }[] = [
  { name: 'Captions (live)', sc: '1.2.4' },
  { name: 'Three flashes or below threshold', sc: '2.3.1' },
  { name: 'Multiple ways', sc: '2.4.5' },
  { name: 'Pointer gestures', sc: '2.5.1' },
  { name: 'Motion actuation', sc: '2.5.4' },
  { name: 'Consistent navigation', sc: '3.2.3' },
  { name: 'Consistent identification', sc: '3.2.4' },
  { name: 'Consistent help', sc: '3.2.6' },
  { name: 'Error prevention (legal, financial, data)', sc: '3.3.4' },
];

export const inventory = CHECK_INVENTORY;

/** Sorted, de-duplicated success criteria a category touches. */
export function criteriaOf(category: CheckCategory): string[] {
  const all = new Set(category.checks.flatMap((c) => c.wcag));
  return [...all].sort((a, b) => {
    const pa = a.split('.').map(Number);
    const pb = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (d !== 0) return d;
    }
    return 0;
  });
}

export const coverage = {
  aaaAdvisories: inventory.wcagCoverage.AAA_suggestions.length,
  checks: inventory.totals.checks,
  levelA: inventory.wcagCoverage.A.length,
  levelAA: inventory.wcagCoverage.AA.length,
  scsCovered: inventory.totals.wcagSCsCovered,
  scsTotal: WCAG22_A_AA_TOTAL,
};
